import { Button, TextInput } from "@mantine/core";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { authClient } from "../../auth/client";
import { useMessages } from "../../i18n/provider";
import styles from "./auth.module.css";
import { AuthFooterLink, AuthLayout } from "./layout";

export function ResetPasswordPage() {
  const t = useMessages();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const request = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("auth.invalidEmail"));
      return;
    }
    setLoading(true);
    const result = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (result.error && result.error.status === 429) {
      setError(t("auth.tooManyAttempts"));
      return;
    }
    setSent(true);
  };

  const update = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (!token) return;
    if (password.length < 8) {
      setError(t("auth.passwordShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.passwordsDiffer"));
      return;
    }
    setLoading(true);
    const result = await authClient.resetPassword({ newPassword: password, token });
    setLoading(false);
    if (result.error) {
      setError(result.error.status === 429 ? t("auth.tooManyAttempts") : t("auth.resetInvalid"));
      return;
    }
    setDone(true);
  };

  return (
    <AuthLayout
      title={t("auth.resetTitle")}
      footer={<AuthFooterLink to="/login" label={t("auth.backToSignIn")} />}
    >
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
      {token ? (
        done ? (
          <div className={styles.sent} role="status">
            {t("auth.resetDone")}
          </div>
        ) : (
          <form className={styles.form} onSubmit={(event) => void update(event)}>
            <TextInput
              label={t("auth.newPassword")}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
              required
            />
            <TextInput
              label={t("auth.confirmPassword")}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.currentTarget.value)}
              required
            />
            <Button type="submit" fullWidth loading={loading}>
              {t("auth.resetAction")}
            </Button>
          </form>
        )
      ) : sent ? (
        <div className={styles.sent} role="status">
          {t("auth.resetSent")}
        </div>
      ) : (
        <>
          <p className={styles.hint} style={{ marginTop: 0 }}>
            {t("auth.resetSubtitle")}
          </p>
          <form className={styles.form} onSubmit={(event) => void request(event)}>
            <TextInput
              label={t("auth.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              required
            />
            <Button type="submit" fullWidth loading={loading}>
              {t("auth.sendLink")}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
